import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, User, ArrowRight, Loader2, AlertCircle, Container } from 'lucide-react';
import { lireSelection } from '../data/offre';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const CommanderSansCompte = () => {
  const navigate = useNavigate();
  const selection = lireSelection();

  // Charger reCAPTCHA v3 dynamiquement (uniquement sur le domaine de production)
  const isProductionDomain = window.location.hostname === 'locationbennechantier.com' || window.location.hostname === 'www.locationbennechantier.com' || window.location.hostname === 'locabennes.com' || window.location.hostname === 'www.locabennes.com';
  useEffect(() => {
    if (!isProductionDomain || !RECAPTCHA_SITE_KEY || document.getElementById('recaptcha-v3-script')) return;
    const script = document.createElement('script');
    script.id = 'recaptcha-v3-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onerror = () => console.warn('reCAPTCHA failed to load');
    document.head.appendChild(script);
  }, [isProductionDomain]);
  const [type, setType] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [codePostal, setCodePostal] = useState('');
  const [societe, setSociete] = useState('');
  const [horsIdf, setHorsIdf] = useState(false);

  const checkIdf = (cp) => {
    if (cp.length === 5) {
      const idfPrefixes = ['75', '77', '78', '91', '92', '93', '94', '95'];
      setHorsIdf(!idfPrefixes.some(p => cp.startsWith(p)));
    } else {
      setHorsIdf(false);
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isValid = () => {
    if (!type || !nom.trim() || !prenom.trim() || !email.trim() || !telephone.trim() || !codePostal.trim()) return false;
    if (type === 'societe' && !societe.trim()) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // reCAPTCHA v3 via window.grecaptcha (graceful fallback if unavailable on preview domain)
    if (RECAPTCHA_SITE_KEY && window.grecaptcha && window.grecaptcha.execute) {
      try {
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'guest_order' });
        if (!token) {
          setIsSubmitting(false);
          return;
        }
      } catch (err) {
        console.warn('reCAPTCHA skipped:', err.message);
      }
    }

    // Stocker les données dans localStorage pour les récupérer dans Commander
    const guestData = {
      type: type === 'societe' ? 'professionnel' : 'particulier',
      name: `${prenom.trim()} ${nom.trim()}`,
      email: email.trim(),
      phone: telephone.trim(),
      postalCode: codePostal.trim(),
      societe: type === 'societe' ? societe.trim() : '',
    };
    localStorage.setItem('ecoripeurs_guest', JSON.stringify(guestData));

    setTimeout(() => {
      navigate('/mon-compte');
    }, 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="guest-form-title">
            Commander sans compte
          </h1>
          <p className="text-gray-600">Renseignez vos informations pour continuer</p>
          {selection && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-50 border border-green-200 px-4 py-2 text-sm text-green-800" data-testid="guest-selection-banner">
              <Container className="h-4 w-4 text-green-600" />
              Votre sélection : <span className="font-bold">Benne {selection.volume} · {selection.type}</span>
              <span className="text-green-700/70">(modifiable à l'étape suivante)</span>
            </div>
          )}
        </div>

        <Card className="p-8 shadow-xl border-2 border-green-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Type selection: PARTICULIER | SOCIÉTÉ */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-3 block">Vous êtes *</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setType('particulier')}
                  className={`flex items-center justify-center gap-2 p-4 border-2 rounded-xl font-bold text-base transition-all ${
                    type === 'particulier'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600 hover:border-green-300'
                  }`}
                  data-testid="guest-type-particulier"
                >
                  <User className="h-5 w-5" />
                  Particulier
                </button>
                <button
                  type="button"
                  onClick={() => setType('societe')}
                  className={`flex items-center justify-center gap-2 p-4 border-2 rounded-xl font-bold text-base transition-all ${
                    type === 'societe'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600 hover:border-green-300'
                  }`}
                  data-testid="guest-type-societe"
                >
                  <Building2 className="h-5 w-5" />
                  Société
                </button>
              </div>
            </div>

            {/* Nom de la société (if société) */}
            {type === 'societe' && (
              <div>
                <Label className="text-sm font-semibold text-gray-700">Nom de la société *</Label>
                <Input
                  data-testid="guest-societe-input"
                  className="mt-1.5 h-11"
                  value={societe}
                  onChange={(e) => setSociete(e.target.value)}
                  placeholder="Nom de votre entreprise"
                />
              </div>
            )}

            {/* Nom */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Nom *</Label>
              <Input
                data-testid="guest-nom-input"
                className="mt-1.5 h-11"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="Dupont"
              />
            </div>

            {/* Prénom */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Prénom *</Label>
              <Input
                data-testid="guest-prenom-input"
                className="mt-1.5 h-11"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                placeholder="Jean"
              />
            </div>

            {/* Email */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Adresse e-mail *</Label>
              <Input
                data-testid="guest-email-input"
                type="email"
                className="mt-1.5 h-11"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jean.dupont@email.fr"
              />
            </div>

            {/* Téléphone */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Numéro de téléphone *</Label>
              <Input
                data-testid="guest-telephone-input"
                type="tel"
                className="mt-1.5 h-11"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                placeholder="06 12 34 56 78"
              />
            </div>

            {/* Code postal du chantier */}
            <div>
              <Label className="text-sm font-semibold text-gray-700">Code postal du chantier *</Label>
              <Input
                data-testid="guest-codepostal-input"
                className="mt-1.5 h-11"
                value={codePostal}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setCodePostal(val);
                  checkIdf(val);
                }}
                placeholder="75001"
                maxLength={5}
              />
              {horsIdf && (
                <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2" data-testid="guest-hors-idf-popup">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-700">Zone non desservie</p>
                    <p className="text-xs text-red-600 mt-0.5">Nous livrons uniquement en Île-de-France (75, 77, 78, 91, 92, 93, 94, 95).</p>
                  </div>
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <p className="text-red-600 text-sm font-medium bg-red-50 p-3 rounded-lg" data-testid="guest-error">
                {error}
              </p>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              disabled={!isValid() || isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              size="lg"
              data-testid="guest-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  Commander ma benne
                  <ArrowRight className="ml-3 h-6 w-6" />
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CommanderSansCompte;
